import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Modal, Table, Td, Th } from "@/components/ui";

interface ConnectionItem {
  ip: string;
  count: number;
  country: string | null;
  country_name: string | null;
}

interface ConnectionsResponse {
  items: ConnectionItem[];
  total: number;
  unique_ips: number;
}

export function ConnectionsModal({
  serverId,
  open,
  onClose,
}: {
  serverId: number;
  open: boolean;
  onClose: () => void;
}) {
  const q = useQuery<ConnectionsResponse>({
    queryKey: ["server-connections", serverId],
    queryFn: () =>
      api<ConnectionsResponse>(`/api/admin/servers/${serverId}/connections`),
    enabled: open,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Источники коннектов"
      maxWidth="max-w-3xl"
    >
      {q.isLoading && <div className="text-zinc-400 text-sm">Идёт сбор (SSH + GeoIP)…</div>}
      {q.isError && <div className="text-red-400 text-sm">Ошибка загрузки</div>}

      {q.data && (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-zinc-400">
              Всего коннектов:{" "}
              <span className="text-zinc-100 font-mono">{q.data.total}</span>
              <span className="mx-2 text-zinc-700">·</span>
              Уникальных IP:{" "}
              <span className="text-zinc-100 font-mono">{q.data.unique_ips}</span>
            </div>
            <Button variant="ghost" onClick={() => q.refetch()} disabled={q.isFetching}>
              {q.isFetching ? "Обновляем…" : "Обновить"}
            </Button>
          </div>

          {q.data.items.length === 0 ? (
            <div className="text-zinc-500 text-sm">Открытых коннектов сейчас нет.</div>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>IP</Th>
                    <Th>страна</Th>
                    <Th align="right" fit>
                      коннектов
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.items.map((it) => (
                    <tr key={it.ip}>
                      <Td className="font-mono text-zinc-100">{it.ip}</Td>
                      <Td className="text-zinc-300">
                        {it.country ? (
                          <span>
                            <span className="font-mono text-zinc-500 mr-2">
                              {it.country}
                            </span>
                            {it.country_name}
                          </span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </Td>
                      <Td align="right" fit className="font-mono text-emerald-300">
                        {it.count}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <p className="text-xs text-zinc-500 mt-4">
            Коннект ≠ активный юзер. Один клиент Telegram держит 2-5 параллельных TCP.
            Если видишь много IP с разных стран — ссылка ушла дальше, чем ты думаешь.
            Пересоздай public_secret и/или включи гео-фильтр.
          </p>
        </>
      )}
    </Modal>
  );
}
